import React, { useMemo, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  TopNavigation,
  SpaceBetween,
  Box,
  Spinner,
} from "@cloudscape-design/components";
import {
  I18nProvider,
  importMessages,
} from "@cloudscape-design/components/i18n";
import "@cloudscape-design/global-styles/index.css";
import "./components/NavbarStyles.css";
import Chat from "./components/Chat";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import Profile from "./components/Profile";
import SecuritySettings from "./components/SecuritySettings";
import CompanySettings from "./components/CompanySettings";
import ForgotPassword from "./components/ForgotPassword";
import AssistantWizard from "./components/AssistantWizard";
import AgentList from "./components/AgentList";
import Dashboard from "./components/Dashboard";
import Billing from "./components/Billing";
import KnowledgeBaseList from "./components/KnowledgeBaseList";
import WorkflowList from "./components/WorkflowList";
import WorkflowAdd from "./components/WorkflowAdd";
import Telephony from "./components/Telephony";
import TelephonyAdd from "./components/TelephonyAdd";
import KnowledgeBaseAdd from "./components/KnowledgeBaseAdd";
import KnowledgeBaseFiles from "./components/KnowledgeBaseFiles";
import Home from "./components/Home";
import ApiManagement from "./components/ApiManagement";
import LlmConfig from "./components/LlmConfig";
import UserManagement from "./components/UserManagement";
import SmtpSettings from "./components/SmtpSettings";
import EmailVerification from "./components/EmailVerification";
import { AuthProvider, useAuth } from "./AuthContext";
import { NotificationProvider } from "./components/NotificationContext";
import { useTheme } from "./ThemeContext";
import Cookies from 'js-cookie';

const LOCALE = "en";
const messages = await importMessages(LOCALE);

// New OAuth Callback Component
const OAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAndHandleAuthCookies } = useAuth();
  const [status, setStatus] = React.useState('Processing authentication...');
  const [error, setError] = React.useState(null);
  const [cookies, setCookies] = React.useState({});

  useEffect(() => {
    // Debug cookies
    const cookiesDebug = {
      user_data: Cookies.get('user_data'),
      access_token: Cookies.get('access_token') ? 'Present' : 'Missing',
      refresh_token: Cookies.get('refresh_token') ? 'Present' : 'Missing'
    };
    
    setCookies(cookiesDebug);
    console.log("Available cookies:", cookiesDebug);
    
    const processCallback = async () => {
      try {
        // Check for error in URL
        const searchParams = new URLSearchParams(location.search);
        const errorParam = searchParams.get('error');
        const errorDetails = searchParams.get('details');
        
        if (errorParam) {
          console.error(`Authentication error: ${errorParam}`);
          if (errorDetails) {
            console.error(`Error details: ${errorDetails}`);
          }
          setError(`${errorParam}${errorDetails ? `: ${errorDetails}` : ''}`);
          setStatus('Authentication failed.');
          // Redirect after showing error
          setTimeout(() => navigate('/signin', { replace: true }), 3000);
          return;
        }
        
        // Check if we have auth cookies
        const processed = checkAndHandleAuthCookies();
        console.log("Auth cookies processed result:", processed);
        
        if (processed) {
          setStatus('Authentication successful! Redirecting...');
          setTimeout(() => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user?.adexcloud_onboarding_pending) {
              navigate('/profile', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }, 1000);
        } else {
          setStatus('Authentication process is completing...');
          // Wait for cookies to be set by backend
          setTimeout(() => {
            console.log("Rechecking cookies...");
            const finalCheck = checkAndHandleAuthCookies();
            console.log("Final cookie check result:", finalCheck);
            
            if (finalCheck) {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              if (user?.adexcloud_onboarding_pending) {
                navigate('/profile', { replace: true });
              } else {
                navigate('/', { replace: true });
              }
            } else {
              setError('Could not complete authentication. Please try again.');
              setStatus('Authentication failed.');
              setTimeout(() => navigate('/signin', { replace: true }), 3000);
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error in OAuth callback processing:', error);
        setError(error.message || 'Unknown error');
        setStatus('Authentication failed. Redirecting to login...');
        setTimeout(() => navigate('/signin', { replace: true }), 3000);
      }
    };
    
    processCallback();
  }, [navigate, location.search, checkAndHandleAuthCookies]);
  
  return (
    <Box padding={{ base: 'xl' }} textAlign="center">
      <Spinner size="large" />
      <Box padding={{ top: 'l' }}>
        <h2>{status}</h2>
        {error && (
          <Box color="text-status-error" padding={{ top: 'm' }}>
            <h3>Error: {error}</h3>
          </Box>
        )}
        <Box padding={{ top: 'm' }}>
          <h4>Cookies Debug:</h4>
          <pre>{JSON.stringify(cookies, null, 2)}</pre>
        </Box>
      </Box>
    </Box>
  );
};


const App = () => {
  const { user, logout, checkAndHandleAuthCookies } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for authentication cookies when app loads
  useEffect(() => {
    // Process any auth cookies that might be present
    checkAndHandleAuthCookies();
    
    // Check URL for error parameters from Google OAuth
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    
    if (error) {
      console.error('Authentication error:', error);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [checkAndHandleAuthCookies, location.search]);

  // Memoize the utilities array to prevent unnecessary recalculations
  const handleUtilityClick = useCallback(({ detail }) => {
    const id = detail?.id;
    const onboardingBlocked = user?.adexcloud_onboarding_pending && id !== 'logout';
    if (onboardingBlocked) return;
    if (id === 'profile') { navigate('/profile'); }
    if (id === 'security') { navigate('/settings/security'); }
    if (id === 'billing' || id === 'balance') { navigate('/billing'); }
    if (id === 'logout') { logout(); }
  }, [navigate, logout, user]);

  const utilities = useMemo(() => {
    if (user) {
      return [
        {
          type: "button",
          iconName: isDark ? "status-positive" : "moon",
          title: isDark ? "Switch to light mode" : "Switch to dark mode",
          ariaLabel: isDark ? "Switch to light mode" : "Switch to dark mode",
          onClick: toggleTheme,
        },
        {
          type: "button",
          iconName: "notification",
          title: "Notifications",
          ariaLabel: "Notifications",
          badge: false,
          disableUtilityCollapse: false,
        },
        {
          type: "menu-dropdown",
          iconSvg: (
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
              <path d="M3 1h10v14l-2.5-1.5L8 15l-2.5-1.5L3 15V1Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              <line x1="5" y1="5" x2="11" y2="5" stroke="currentColor" strokeWidth="1.5" />
              <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="1.5" />
              <line x1="5" y1="11" x2="8" y2="11" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          ),
          ariaLabel: "Billing",
          title: "Billing",
          items: [
            { id: "billing", text: "Billing & Packs" },
            { id: "balance", text: "Credit Balance" },
          ],
          onItemClick: handleUtilityClick,
        },
        {
          type: "menu-dropdown",
          text: user.username,
          description: user.email,
          iconName: "user-profile",
          items: [
            { id: "profile", text: "Profile" },
            { id: "security", text: "Security" },
            { id: "logout", text: "Logout" },
          ],
          onItemClick: handleUtilityClick,
        },
      ];
    } else {
      return [
        {
          type: "button",
          iconName: isDark ? "status-positive" : "moon",
          title: isDark ? "Switch to light mode" : "Switch to dark mode",
          ariaLabel: isDark ? "Switch to light mode" : "Switch to dark mode",
          onClick: toggleTheme,
        },
        {
          type: "button",
          variant: "primary",
          text: "Signin",
          href: "/signin",
        },
      ];
    }
  }, [user, isDark, toggleTheme, handleUtilityClick]);

  const authRoutes = ["/signin", "/signup", "/forgot-password"];
  const showTopNav = !authRoutes.includes(location.pathname);

  return (
    <I18nProvider locale={LOCALE} messages={[messages]}>
      {showTopNav && (
        <TopNavigation
          identity={{
            href: "/dashboard",
            logo: {
              src: "/logoadexcloud.png",
              alt: "Adexgénie"
            }
          }}
          utilities={utilities}
          onFollow={(event) => {
            event.preventDefault();
            if (user?.adexcloud_onboarding_pending) {
              navigate('/profile', { replace: true });
            } else {
              navigate('/dashboard');
            }
          }}
        />
      )}

      <SpaceBetween size="xxxs">
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Home />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/chat"
            element={
              user ? (
                <Chat user={user} onLogout={logout} />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                <Dashboard />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/billing"
            element={
              user ? (
                <Billing />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route
            path="/profile"
            element={user ? <Profile /> : <Navigate to="/signin" />}
          />
          <Route
            path="/settings/security"
            element={user ? <SecuritySettings /> : <Navigate to="/signin" />}
          />
          <Route
            path="/settings/company"
            element={user ? <CompanySettings /> : <Navigate to="/signin" />}
          />
          <Route
            path="/Wizard"
            element={
              user ? (
                <AssistantWizard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/agents"
            element={
              user ? (
                <AgentList user={user} />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/listagent"
            element={<Navigate to="/agents" replace />}
          />
          <Route
            path="/knowledge-bases"
            element={
              user ? (
                <KnowledgeBaseList />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/workflows"
            element={
              user ? (
                <WorkflowList />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/workflow/new"
            element={
              user ? (
                <WorkflowAdd />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/telephony"
            element={
              user ? (
                <Telephony />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/telephony/new"
            element={
              user ? (
                <TelephonyAdd />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/knowledge-base/new"
            element={
              user ? (
                <KnowledgeBaseAdd />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/knowledge-base-files"
            element={
              user ? (
                <KnowledgeBaseFiles />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />

          {/* Super Admin routes */}
          <Route
            path="/admin/api-management"
            element={
              user ? (
                <ApiManagement />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/admin/llm-config"
            element={
              user ? (
                <LlmConfig />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/admin/smtp-settings"
            element={
              user ? (
                <SmtpSettings />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              user ? (
                <UserManagement />
              ) : (
                <Navigate to="/signin" />
              )
            }
          />

          {/* OAuth callback route */}
          <Route path="/oauth/callback" element={<OAuthCallback />} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </SpaceBetween>
    </I18nProvider>
  );
};

const AppWrapper = () => {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <I18nProvider locale={LOCALE} messages={messages}>
            <App />
          </I18nProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
};

export default AppWrapper;
